import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  getSecurityEvents,
  getAdminActions,
  getSupabaseSecurityAdvisor,
  getSupabaseAuthLogs
} from './adminService';
import {
  Shield,
  AlertTriangle,
  AlertCircle,
  Activity,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  User,
  Clock,
  FileText,
  Info,
  XCircle,
  CheckCircle,
  ShieldCheck
} from 'lucide-react';

export default function AdminSecurityPage() {
  const { isArabic } = useOutletContext();
  const [activeTab, setActiveTab] = useState('security');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [securityAdvisor, setSecurityAdvisor] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  // Load data when tab or filters change
  useEffect(() => {
    if (activeTab === 'security') {
      loadSecurityAdvisor();
    } else {
      loadAuditLogs();
    }
  }, [activeTab, severityFilter, categoryFilter, actionFilter]);

  // Load Supabase Security Advisor
  async function loadSecurityAdvisor() {
    try {
      setLoading(true);
      setError(null);

      const data = await getSupabaseSecurityAdvisor();
      let advisorData = data.security_advisor || [];

      // Apply filters
      if (severityFilter !== 'all') {
        advisorData = advisorData.filter(item => item.severity === severityFilter);
      }
      if (categoryFilter !== 'all') {
        advisorData = advisorData.filter(item => item.category === categoryFilter);
      }

      setSecurityAdvisor(advisorData);
    } catch (err) {
      console.error('Error loading security advisor:', err);
      setError(err.message || 'Failed to load security advisor');
    } finally {
      setLoading(false);
    }
  }

  // Load Supabase auth audit logs
  async function loadAuditLogs() {
    try {
      setLoading(true);
      setError(null);

      const filters = { limit: 100 };
      if (actionFilter !== 'all') filters.action = actionFilter;

      const data = await getSupabaseAuthLogs(filters);
      setAuditLogs(data.audit_logs || []);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }

  // Get severity badge
  function getSeverityBadge(severity) {
    const severityConfig = {
      info: {
        label: isArabic ? 'معلومات' : 'Info',
        bg: '#e3f2fd',
        color: '#2196F3',
        icon: Info
      },
      warning: {
        label: isArabic ? 'تحذير' : 'Warning',
        bg: '#fff3e0',
        color: '#FF9800',
        icon: AlertTriangle
      },
      error: {
        label: isArabic ? 'خطأ' : 'Error',
        bg: '#ffebee',
        color: '#f44336',
        icon: AlertCircle
      },
      critical: {
        label: isArabic ? 'حرج' : 'Critical',
        bg: '#fce4ec',
        color: '#e91e63',
        icon: XCircle
      }
    };

    const config = severityConfig[severity] || severityConfig.info;
    const Icon = config.icon;

    return (
      <span style={{
        padding: '6px 12px',
        background: config.bg,
        color: config.color,
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6
      }}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  }

  // Filter options
  const severityOptions = [
    { value: 'all', label: isArabic ? 'الكل' : 'All' },
    { value: 'info', label: isArabic ? 'معلومات' : 'Info' },
    { value: 'warning', label: isArabic ? 'تحذير' : 'Warning' },
    { value: 'error', label: isArabic ? 'خطأ' : 'Error' },
    { value: 'critical', label: isArabic ? 'حرج' : 'Critical' }
  ];

  const categoryOptions = [
    { value: 'all', label: isArabic ? 'الكل' : 'All' },
    { value: 'Database-Level Security Alerts', label: isArabic ? 'تنبيهات أمان قاعدة البيانات' : 'Database-Level Security Alerts' },
    { value: 'Row Level Security', label: isArabic ? 'أمان مستوى الصفوف' : 'Row Level Security' },
    { value: 'Authentication', label: isArabic ? 'المصادقة' : 'Authentication' },
    { value: 'Security Events', label: isArabic ? 'الأحداث الأمنية' : 'Security Events' },
    { value: 'API Security', label: isArabic ? 'أمان API' : 'API Security' },
    { value: 'Overall Security', label: isArabic ? 'الأمان العام' : 'Overall Security' }
  ];

  const actionOptions = [
    { value: 'all', label: isArabic ? 'الكل' : 'All' },
    // Authentication
    { value: 'login', label: isArabic ? 'تسجيل دخول' : 'Login' },
    { value: 'logout', label: isArabic ? 'تسجيل خروج' : 'Logout' },
    { value: 'signup', label: isArabic ? 'تسجيل' : 'Signup' },
    { value: 'student_signup', label: isArabic ? 'تسجيل طالب' : 'Student Signup' },
    { value: 'teacher_signup', label: isArabic ? 'تسجيل معلم' : 'Teacher Signup' },
    // XP & Gamification
    { value: 'xp_awarded', label: isArabic ? 'منح XP' : 'XP Awarded' },
    { value: 'level_up', label: isArabic ? 'ترقية مستوى' : 'Level Up' },
    // Bookings
    { value: 'booking_request', label: isArabic ? 'طلب حجز' : 'Booking Request' },
    { value: 'booking_created', label: isArabic ? 'إنشاء حجز' : 'Booking Created' },
    { value: 'booking_approved', label: isArabic ? 'موافقة حجز' : 'Booking Approved' },
    { value: 'booking_accepted', label: isArabic ? 'قبول حجز' : 'Booking Accepted' },
    { value: 'booking_declined', label: isArabic ? 'رفض حجز' : 'Booking Declined' },
    { value: 'reschedule_requested', label: isArabic ? 'طلب إعادة جدولة' : 'Reschedule Requested' },
    { value: 'reschedule_accepted', label: isArabic ? 'قبول إعادة جدولة' : 'Reschedule Accepted' },
    { value: 'reschedule_declined', label: isArabic ? 'رفض إعادة جدولة' : 'Reschedule Declined' },
    // Admin Actions
    { value: 'approve', label: isArabic ? 'موافقة' : 'Approve' },
    { value: 'reject', label: isArabic ? 'رفض' : 'Reject' },
    { value: 'suspend', label: isArabic ? 'تعليق' : 'Suspend' },
    { value: 'activate', label: isArabic ? 'تفعيل' : 'Activate' },
    { value: 'delete', label: isArabic ? 'حذف' : 'Delete' }
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontSize: 32,
          fontWeight: 700,
          color: '#232B3E',
          margin: '0 0 8px 0'
        }}>
          {isArabic ? 'الأمان والمراجعة' : 'Security & Audit'}
        </h1>
        <p style={{ color: '#666', margin: 0, fontSize: 16 }}>
          {isArabic
            ? 'مراقبة الأحداث الأمنية وسجل إجراءات المسؤولين'
            : 'Monitor security events and admin action logs'}
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 24,
        borderBottom: '2px solid #e0e0e0'
      }}>
        <button
          onClick={() => setActiveTab('security')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'security' ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: activeTab === 'security' ? '3px solid #f44336' : '3px solid transparent',
            color: activeTab === 'security' ? '#f44336' : '#666',
            fontWeight: activeTab === 'security' ? 600 : 400,
            fontSize: 16,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.2s',
            marginBottom: -2
          }}
        >
          <ShieldCheck size={20} />
          {isArabic ? 'مستشار الأمان' : 'Security Advisor'}
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'audit' ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: activeTab === 'audit' ? '3px solid #2196F3' : '3px solid transparent',
            color: activeTab === 'audit' ? '#2196F3' : '#666',
            fontWeight: activeTab === 'audit' ? 600 : 400,
            fontSize: 16,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.2s',
            marginBottom: -2
          }}
        >
          <Activity size={20} />
          {isArabic ? 'سجلات المراجعة' : 'Audit Logs'}
        </button>
      </div>

      {/* Filters */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: 20,
        marginBottom: 24,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12
      }}>
        {activeTab === 'security' ? (
          <>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 6 }}>
                {isArabic ? 'الخطورة' : 'Severity'}
              </label>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                style={selectStyle}
              >
                {severityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 6 }}>
                {isArabic ? 'الفئة' : 'Category'}
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={selectStyle}
              >
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 6 }}>
                {isArabic ? 'نوع الإجراء' : 'Action Type'}
              </label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                style={selectStyle}
              >
                {actionOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <AlertCircle size={20} color="#c00" />
          <p style={{ color: '#c00', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Content */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{
              width: 48,
              height: 48,
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #FF7426',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p style={{ color: '#666' }}>
              {isArabic ? 'جاري التحميل...' : 'Loading...'}
            </p>
          </div>
        ) : activeTab === 'security' ? (
          securityAdvisor.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center' }}>
              <ShieldCheck size={48} color="#ccc" style={{ margin: '0 auto 16px' }} />
              <p style={{ color: '#666', fontSize: 16 }}>
                {isArabic ? 'لا توجد توصيات أمنية' : 'No security recommendations found'}
              </p>
            </div>
          ) : (
            <div>
              {securityAdvisor.map((item) => (
                <SecurityAdvisorCard
                  key={item.id}
                  item={item}
                  isArabic={isArabic}
                  isExpanded={expandedItem === item.id}
                  onToggleExpand={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                  getSeverityBadge={getSeverityBadge}
                />
              ))}
            </div>
          )
        ) : (
          auditLogs.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center' }}>
              <Activity size={48} color="#ccc" style={{ margin: '0 auto 16px' }} />
              <p style={{ color: '#666', fontSize: 16 }}>
                {isArabic ? 'لا توجد سجلات' : 'No audit logs found'}
              </p>
            </div>
          ) : (
            <div>
              {auditLogs.map((log) => (
                <AuditLogCard
                  key={log.id}
                  log={log}
                  isArabic={isArabic}
                  isExpanded={expandedItem === log.id}
                  onToggleExpand={() => setExpandedItem(expandedItem === log.id ? null : log.id)}
                />
              ))}
            </div>
          )
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// SecurityAdvisorCard Component
function SecurityAdvisorCard({ item, isArabic, isExpanded, onToggleExpand, getSeverityBadge }) {
  return (
    <div style={{
      borderBottom: '1px solid #f0f0f0',
      padding: 20,
      background: isExpanded ? '#f7f9fb' : '#fff',
      transition: 'background 0.2s'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer'
      }}
      onClick={onToggleExpand}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            {getSeverityBadge(item.severity)}
            <span style={{
              padding: '4px 10px',
              background: '#e3f2fd',
              color: '#2196F3',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 500
            }}>
              {item.category}
            </span>
          </div>

          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#232B3E', margin: '0 0 8px 0' }}>
            {item.title}
          </h3>

          <p style={{ color: '#666', margin: '0 0 8px 0', fontSize: 14 }}>
            {item.description}
          </p>

          <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#999' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={14} />
              <span>{new Date(item.created_at).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {isExpanded && (
        <div style={{
          marginTop: 16,
          padding: 16,
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e0e0e0'
        }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#4CAF50', margin: '0 0 12px 0' }}>
            {isArabic ? '💡 التوصية' : '💡 Recommendation'}
          </h4>
          <p style={{ fontSize: 14, color: '#232B3E', margin: '0 0 16px 0' }}>
            {item.recommendation}
          </p>

          {item.affected_resources && item.affected_resources.length > 0 && (
            <>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: '#FF9800', margin: '0 0 12px 0' }}>
                {isArabic ? '⚠️ الموارد المتأثرة' : '⚠️ Affected Resources'}
              </h4>
              <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
                {item.affected_resources.map((resource, index) => (
                  <li key={index} style={{ marginBottom: 4 }}>{resource}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// AuditLogCard Component
function AuditLogCard({ log, isArabic, isExpanded, onToggleExpand }) {
  const actionColors = {
    // Authentication
    login: '#4CAF50',
    logout: '#9E9E9E',
    signup: '#2196F3',
    student_signup: '#2196F3',
    teacher_signup: '#2196F3',
    password_recovery: '#FF9800',
    token_refreshed: '#00BCD4',
    user_updated: '#9C27B0',
    // Booking
    booking_request: '#2196F3',
    booking_approved: '#4CAF50',
    booking_declined: '#f44336',
    // Admin Actions
    approve: '#4CAF50',
    reject: '#f44336',
    suspend: '#FF9800',
    activate: '#4CAF50',
    create: '#4CAF50',
    update: '#2196F3',
    delete: '#f44336',
    view: '#9E9E9E'
  };

  const color = actionColors[log.action] || '#666';

  // Determine if action was successful
  const isSuccessful = log.success !== false;

  return (
    <div style={{
      borderBottom: '1px solid #f0f0f0',
      padding: 20,
      background: isExpanded ? '#f7f9fb' : '#fff',
      transition: 'background 0.2s'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer'
      }}
      onClick={onToggleExpand}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{
              padding: '6px 12px',
              background: `${color}20`,
              color: color,
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 600
            }}>
              {log.action ? log.action.toUpperCase() : 'UNKNOWN'}
            </span>
            {isSuccessful ? (
              <CheckCircle size={16} color="#4CAF50" />
            ) : (
              <XCircle size={16} color="#f44336" />
            )}
            <span style={{
              padding: '4px 8px',
              background: '#f5f5f5',
              color: '#666',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 500
            }}>
              {log.resource_type || 'auth'}
            </span>
          </div>

          <p style={{ color: '#666', margin: '0 0 8px 0', fontSize: 14 }}>
            {isArabic ? 'المستخدم:' : 'User:'} <strong>{log.actor_email || log.actor_id || 'Unknown'}</strong>
          </p>

          {log.description && (
            <p style={{ color: '#888', margin: '0 0 8px 0', fontSize: 13, fontStyle: 'italic' }}>
              {log.description}
            </p>
          )}

          <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#999', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={14} />
              <span>{new Date(log.timestamp).toLocaleString()}</span>
            </div>
            {log.ip_address && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Info size={14} />
                <span>IP: {log.ip_address}</span>
              </div>
            )}
            {log.user_agent && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <User size={14} />
                <span>
                  {log.user_agent.includes('Mobile') ? '📱 Mobile' :
                   log.user_agent.includes('Windows') ? '💻 Windows' :
                   log.user_agent.includes('Mac') ? '🍎 Mac' : '🖥️ Desktop'}
                </span>
              </div>
            )}
          </div>
        </div>

        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {isExpanded && log.payload && (
        <div style={{
          marginTop: 16,
          padding: 16,
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e0e0e0'
        }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#666', margin: '0 0 12px 0' }}>
            {isArabic ? 'التفاصيل الكاملة' : 'Full Details'}
          </h4>
          <pre style={{
            fontSize: 12,
            color: '#232B3E',
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
            maxHeight: 300,
            overflow: 'auto'
          }}>
            {JSON.stringify(log.payload, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// Styles
const selectStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  fontSize: 14,
  cursor: 'pointer',
  background: '#fff',
  outline: 'none'
};
